import { useMemo } from "react";
import moment from "moment-timezone";

import { defaultChartOptions } from "../../helpers/charts";
import Chart from "./Chart";
import config from "../../constants/charts";
import { useMetricsState } from "../../context/metrics";

import { getChartConfig } from "../../helpers/getChartConfig";
import { useChartData } from "../../data/useChartData";
import { roundTo } from "./Grouping";
import { ClientField } from "../../api/clients";

export type AccumulatableDataPoint = {
  x: number;
  y: number;
  values: {
    run_off_in?: number;
    run_off_out?: number;
  };
};
function getAccumulatedDatasets(
  data: AccumulatableDataPoint[],
  calculateRunOffPercent: boolean,
  runoffMultiplier = 1
) {
  const groupedData = new Map<
    string,
    Map<
      number,
      { x: number; y: number; run_off_in?: number; run_off_out?: number }
    >
  >();

  const dateKeyFormat = "YYYY-MM-DD HH:mm";

  // Group data based on date
  data.forEach((record) => {
    const date = moment(record.x).format(dateKeyFormat);
    const timeData: ReturnType<typeof groupedData.get> =
      groupedData.get(date) ?? new Map();
    timeData.set(record.x, {
      x: record.x,
      y: record.y > 0 ? record.y * runoffMultiplier : 0,
      run_off_in: record.values?.run_off_in,
      run_off_out: record.values?.run_off_out,
    });
    groupedData.set(date, timeData);
  });

  // Sum data based on groups
  let accumulated = {
    y: 0,
    run_off_in: 0,
    run_off_out: 0,
  };
  let previousDay: string | null = null;
  return Array.from(groupedData.entries()).map(([date, timeData]) => {
    // reset accumulated when day changes (handles DTU offline at midnight)
    const dateMoment = moment(date, dateKeyFormat);
    const currentDay = dateMoment.format("YYYY-MM-DD");
    if (previousDay !== null && currentDay !== previousDay) {
      accumulated = {
        y: 0,
        run_off_in: 0,
        run_off_out: 0,
      };
    }
    previousDay = currentDay;
    const first = Array.from(timeData.values())[0];
    if (calculateRunOffPercent) {
      for (const record of timeData.values()) {
        accumulated.run_off_in += record.run_off_in ?? 0;
        accumulated.run_off_out += record.run_off_out ?? 0;
      }
      accumulated.y =
        accumulated.run_off_in > 0 && accumulated.run_off_out > 0
          ? (accumulated.run_off_out / accumulated.run_off_in) * 100
          : accumulated.y;
    } else {
      for (const record of timeData.values()) {
        accumulated.y += record.y;
      }
    }
    return {
      x: roundTo(first.x, "15 mins"),
      ...accumulated,
    };
  });
}

const ChartAccumulatedRunOff: React.FC<{
  field: ClientField;
  chart: { id: keyof typeof config; group: string };
  range: any;
  theme: any;
  chartEvents: any;
}> = ({ field, chart, range, theme, chartEvents }) => {
  const { chartData } = useChartData(chart.group, range, field.id);

  const runoffMultiplier = field.chart_runoff_multiplication_factor ?? 1;

  const metrics = useMetricsState();
  const chartConfig = useMemo(() => {
    const axis = [{ yAxisID: "y" }, { yAxisID: "y2" }];
    const initialConfig = getChartConfig(chartData, {
      chartType: chart.id,
      theme,
      metrics,
    });
    const datasets = initialConfig.datasets.map((item, i) => {
      const isPercent = item?.label === "Run Off %";
      const data = getAccumulatedDatasets(
        item?.data as AccumulatableDataPoint[],
        isPercent,
        runoffMultiplier
      );
      return {
        ...item,
        data,
        ...axis[isPercent ? 0 : 1],
      };
    });
    return {
      datasets,
    };
  }, [field, chartData, theme, chart.id, metrics, runoffMultiplier]);

  const chartOptions = useMemo(() => {
    const options = defaultChartOptions(
      config[chart.id].axis,
      config[chart.id].legend,
      range,
      chartEvents
    );
    return {
      ...options,
      scales: {
        ...options.scales,
        y: {
          type: "linear",
          display: true,
          position: "left",
          title: {
            display: true,
            text: "% Run-Off",
          },
          beginAtZero: true,
          suggestedMax: 100,
        },
        y2: {
          type: "linear",
          display: true,
          position: "right",
          title: {
            display: true,
            text: "ml",
          },
          beginAtZero: true,
          grid: {
            borderDash: [4, 10],
          },
        },
      },
    };
  }, [chart.id, range]);

  return (
    <Chart
      chartRef={chartEvents.chartRef}
      data={chartConfig}
      options={chartOptions}
    />
  );
};

export default ChartAccumulatedRunOff;
